import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Grid, 
  Paper, 
  Typography, 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import CancelIcon from '@mui/icons-material/Cancel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
// Remove this line: import statusData from '../../components/Data/Izsoftware_status.json';

const Status = () => {
  const PRIMARY = '#004d99';
  const [zetScoreServices, setZetScoreServices] = useState([]);
  const [zetCollectServices, setZetCollectServices] = useState([]);
  const [, setMetadata] = useState({});
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStatusData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('https://raw.githubusercontent.com/IZSoftware/product-status/refs/heads/main/Izsoftware_status.json');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      setZetScoreServices(data.zetScoreServices || []);
      setZetCollectServices(data.zetCollectServices || []);
      setMetadata(data.metadata || {});
      setLastUpdated(new Date());
      
    } catch (err) {
      console.error('Error fetching status data:', err);
      setError(err.message || 'Failed to fetch status data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatusData();
    
    // Optional: Set up periodic refresh (e.g., every 5 minutes)
    const intervalId = setInterval(fetchStatusData, 5 * 60 * 1000);
    
    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  const zetScoreWithProduct = zetScoreServices.map((item, index) => ({ 
    ...item, 
    product: index === 0 ? 'ZetScore' : ''
  }));
  
  const zetCollectWithProduct = zetCollectServices.map((item, index) => ({ 
    ...item, 
    product: index === 0 ? 'ZetCollect' : ''
  }));

  // Combine all services
  const allServices = [...zetScoreWithProduct, ...zetCollectWithProduct];

  const getStatusIcon = (status) => {
    switch(status) {
      case 'Operational':
        return <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 32 }} />;
      case 'Under Maintenance':
        return <SettingsIcon sx={{ color: PRIMARY, fontSize: 32 }} />;
      case 'Major Outage':
        return <CancelIcon sx={{ color: '#d32f2f', fontSize: 32 }} />;
      default:
        return null;
    }
  };

  // Calculate counts
  const zetScoreOperational = zetScoreServices.filter(item => item.status === 'Operational').length;
  const zetScoreMaintenance = zetScoreServices.filter(item => item.status === 'Under Maintenance').length;
  const zetScoreTotal = zetScoreServices.length;

  const zetCollectOperational = zetCollectServices.filter(item => item.status === 'Operational').length;
  const zetCollectMaintenance = zetCollectServices.filter(item => item.status === 'Under Maintenance').length;
  const zetCollectTotal = zetCollectServices.length;

  // Loading state
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', p: 3 }}>
        <Container maxWidth="md">
          <Alert 
            severity="error" 
            sx={{ mt: 4 }}
            action={
              <Button color="inherit" size="small" onClick={fetchStatusData}>
                Retry
              </Button>
            }
          >
            Error loading status data: {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', pt: { xs: '20px', sm: '30px', md: '40px' } }}>
      <Container maxWidth={false} sx={{ px: { xs: 5, sm: 3, md: 0 } }}>
        <Grid container spacing={6} justifyContent="center" alignItems="center">
          <Grid item xs={12} md={9.5}>
            <Grid container spacing={6} alignItems="center" justifyContent="center">
              <Grid item xs={12}>
                <Box sx={{ py: { xs: 1, sm: 2 } }}>

                  {/* Status Indicators - Single column on mobile, row on desktop */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'center', 
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 2, sm: 4, md: 6, lg: 8 }, 
                    mb: { xs: 4, sm: 5 }, 
                    flexWrap: 'wrap',
                    width: '100%'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
                      <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 28 }} />
                      <Typography sx={{ color: '#374151', fontWeight: 600, fontSize: '1.2rem' }}>Operational</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
                      <SettingsIcon sx={{ color: PRIMARY, fontSize: 28 }} />
                      <Typography sx={{ color: '#374151', fontWeight: 600, fontSize: '1.2rem' }}>Under Maintenance</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
                      <CancelIcon sx={{ color: '#d32f2f', fontSize: 28 }} />
                      <Typography sx={{ color: '#374151', fontWeight: 600, fontSize: '1.2rem' }}>Major Outage</Typography>
                    </Box>
                  </Box>

                  {/* Current Status Banner - High visibility */}
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: { xs: 3, sm: 4, md: 5 }, 
                      mb: { xs: 4, sm: 5 }, 
                      bgcolor: '#f0f7ff',
                      border: '1px solid',
                      borderColor: '#e0e7ff',
                      borderRadius: 2
                    }}
                  >
                    <Grid container spacing={3} alignItems="center" justifyContent="space-between">
                      <Grid item xs={12} md={6}>
                        <Typography variant="h3" sx={{ fontWeight: 700, color: '#111827', mb: 1, fontSize: { xs: '2rem', sm: '2.5rem' } }}>
                          Product Health Overview
                        </Typography>
                        <Typography sx={{ color: '#4b5563', fontSize: { xs: '1rem', sm: '1.2rem' } }}>
                          Last updated: {lastUpdated.toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', gap: { xs: 2, sm: 4 }, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#2e7d32' }} />
                            <Typography sx={{ color: '#374151', fontWeight: 600, fontSize: { xs: '1rem', sm: '1.2rem' } }}>
                              ZetScore: {zetScoreOperational}/{zetScoreTotal} Operational
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: PRIMARY }} />
                            <Typography sx={{ color: '#374151', fontWeight: 600, fontSize: { xs: '1rem', sm: '1.2rem' } }}>
                              ZetCollect: {zetCollectOperational}/{zetCollectTotal} Operational
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Combined Status Table */}
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                      <Typography variant="h5" sx={{ fontWeight: 600, color: '#374151', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                        Product Status Details
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ px: { xs: 2, sm: 3 }, py: 1, bgcolor: '#e8f5e9', borderRadius: 5 }}>
                          <Typography sx={{ color: '#2e7d32', fontSize: { xs: '0.9rem', sm: '1.1rem' }, fontWeight: 600 }}>
                            ZetScore: {zetScoreOperational} Operational • {zetScoreMaintenance} Maintenance
                          </Typography>
                        </Box>
                        <Box sx={{ px: { xs: 2, sm: 3 }, py: 1, bgcolor: '#e6f0ff', borderRadius: 5 }}>
                          <Typography sx={{ color: PRIMARY, fontSize: { xs: '0.9rem', sm: '1.1rem' }, fontWeight: 600 }}>
                            ZetCollect: {zetCollectOperational} Operational • {zetCollectMaintenance} Maintenance
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    
                    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2 }}>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f9fafb' }}>
                            <TableCell sx={{ fontWeight: 700, color: '#4b5563', py: { xs: 2, sm: 3 }, fontSize: { xs: '1rem', sm: '1.2rem' } }}>Product</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#4b5563', py: { xs: 2, sm: 3 }, fontSize: { xs: '1rem', sm: '1.2rem' } }}>Service</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, color: '#4b5563', py: { xs: 2, sm: 3 }, fontSize: { xs: '1rem', sm: '1.2rem' } }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#4b5563', py: { xs: 2, sm: 3 }, fontSize: { xs: '1rem', sm: '1.2rem' } }}>Details</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {allServices.map((item, index) => {
                            const isFirstZetCollect = index === zetScoreServices.length;
                            
                            return (
                              <React.Fragment key={`service-${index}`}>
                                {/* Thicker diagonal line separator between ZetScore and ZetCollect */}
                                {isFirstZetCollect && (
                                  <TableRow>
                                    <TableCell 
                                      colSpan={4} 
                                      sx={{ 
                                        p: 0, 
                                        borderBottom: 'none',
                                        position: 'relative',
                                        height: '32px',
                                        '&::before': {
                                          content: '""',
                                          position: 'absolute',
                                          top: '50%',
                                          left: 0,
                                          right: 0,
                                          height: '5px',
                                          background: 'linear-gradient(135deg, transparent 49.5%, #64748b 49.5%, #64748b 50.5%, transparent 50.5%)',
                                          backgroundSize: '25px 25px',
                                          backgroundRepeat: 'repeat-x',
                                        }
                                      }}
                                    />
                                  </TableRow>
                                )}
                                <TableRow sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                                  <TableCell sx={{ 
                                    fontWeight: 700, 
                                    color: item.product === 'ZetScore' ? '#2e7d32' : PRIMARY, 
                                    py: { xs: 2, sm: 3 }, 
                                    fontSize: { xs: '1.2rem', sm: '1.4rem' }  // Increased product name font size
                                  }}>
                                    {item.product}
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 500, color: '#111827', py: { xs: 2, sm: 3 }, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                                    {item.service}
                                  </TableCell>
                                  <TableCell align="center" sx={{ py: { xs: 2, sm: 3 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      {getStatusIcon(item.status)}
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ color: '#6b7280', py: { xs: 2, sm: 3 }, fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                                    {item.reason}
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  {/* Full Overview Title and Buttons below the table */}
                  <Box sx={{ mt: 4, mb: 2, textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', fontSize: { xs: '1.8rem', sm: '2.2rem' }, mb: 3 }}>
                      Full Overview
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        href="https://zetscore.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        endIcon={<OpenInNewIcon />}
                        sx={{
                          bgcolor: '#2e7d32',
                          '&:hover': {
                            bgcolor: '#1b5e20',
                          },
                          fontWeight: 600,
                          textTransform: 'none',
                          px: 4,
                          py: 1.5,
                          fontSize: '1rem'
                        }}
                      >
                        Visit ZetScore
                      </Button>
                      <Button
                        variant="contained"
                        href="https://zetcollect.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        endIcon={<OpenInNewIcon />}
                        sx={{
                          bgcolor: PRIMARY,
                          '&:hover': {
                            bgcolor: '#003366',
                          },
                          fontWeight: 600,
                          textTransform: 'none',
                          px: 4,
                          py: 1.5,
                          fontSize: '1rem'
                        }}
                      >
                        Visit ZetCollect
                      </Button>
                    </Box>
                  </Box>

                  {/* Footer */}
                  <Box sx={{ mt: { xs: 4, sm: 5 }, textAlign: 'center' }}>
                    <Typography sx={{ color: '#9ca3af', fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                      For support inquiries, please contact your system administrator
                    </Typography>
                  </Box>

                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Status;